const UsersService = function (APIService, PermissionService, $q, ErrorService) {

    const self = this;

    /**
     * Fetches a user by id
     * @param userId {number}
     * @returns {*}
     */
    self.getUserById = function (userId) {
        const { storeId, callCenterId, brandId } = this.getUserStoreBrandAndCallCenterContexts();
        return APIService.Users.getUserById(
            userId,
            brandId,
            storeId,
            callCenterId);
    };

    /**
     * Gets all users for a specific context
     * @param take {number}
     * @param skip {number}
     * @param userContext {Dashboard.Models.Permissions.RoleContext}
     * @param filterText {null|string}
     * @returns {*|HTTPPromise}
     */
    self.getAllUsersForContext = function (take,
                                           skip,
                                           userContext,
                                           filterText = null) {
        const contextCollection = [];
        switch (userContext.ContextType) {
            case Dashboard.ContextTypes.BrandContext:
                contextCollection.push(userContext.BrandId);
                break;
            case Dashboard.ContextTypes.CallCenterContext:
                contextCollection.push(userContext.CallCenterId);
                break;
            case Dashboard.ContextTypes.StoreContext:
                contextCollection.push(userContext.StoreId);
                break;
        }
        return APIService.Users.getUsersForContext(take,
            skip,
            userContext,
            contextCollection,
            filterText);
    };

    /**
     * Gets all users not in a specific context
     * @param take {number}
     * @param skip {number}
     * @param userContext {Dashboard.Models.Permissions.RoleContext}
     * @returns {*|HTTPPromise}
     */
    self.getAllUsersNotInContext = function (take, skip, userContext) {
        const deferred = $q.defer();
        let contextCollection = [];
        switch (userContext.ContextType) {
            case Dashboard.ContextTypes.BrandContext:
                contextCollection = PermissionService.getUserContextBrandIds();
                contextCollection = _.without(contextCollection, userContext.BrandId);
                break;
            case Dashboard.ContextTypes.CallCenterContext:
                contextCollection = PermissionService.getUserContextCallCenterIds();
                contextCollection = _.without(contextCollection, userContext.CallCenterId);
                break;
            case Dashboard.ContextTypes.StoreContext:
                contextCollection = PermissionService.getUserContextStoreIds();
                contextCollection = _.without(contextCollection, userContext.StoreId);
                break;
        }
        if (contextCollection.length === 0) {
            var result = { data: { Users: [] } };
            deferred.resolve(result);
            return deferred.promise;
        }

        APIService.Users.getUsersForContext(take, skip, userContext, contextCollection).then(
            function (result) {
                deferred.resolve(result);
            },
            function (result) {
                deferred.reject(result);
            }
        );
        return deferred.promise;
    };

    /**
     * Returns a user that can be managed by the logged in user by email
     */
    self.getManageableUserByEmail = function (email) {
        const brandIds = PermissionService.getUserContextBrandIds();
        const callCenterIds = PermissionService.getUserContextCallCenterIds();
        const storeIds = PermissionService.getUserContextStoreIds();

        return APIService.Users.getManageableUserByEmail(email, brandIds, callCenterIds, storeIds);
    };

    self.getUserByEmail = (email) => {
        if (!email) {
            throw Error("Email must be provided as a parameter")
        }
        return APIService.Users.getUserByEmail(email)
    }

    self.getRolesUserIsAllowedToManage = async function (context) {
        try {
            let rolesForUser = [];
            let roleContextMapping = [];

            const getUserManageableRoles = async function (context) {
                const { data } = await APIService.Users.getManageableRoles(context)
                rolesForUser = data;
            };

            // Gets the role context mapping which returns which roles requires which contexts
            const getRoleContextMapping = async function () {
                const { data } = await APIService.Roles.getRoleContextRequirementMapping()
                roleContextMapping = Object.values(data);
            };

            await getUserManageableRoles(context)
            await getRoleContextMapping();

            return rolesForUser.map(roleForUser => {
                const role = new Role().initialiseRole(roleForUser)
                const roleContext = roleContextMapping.find(mapping => mapping.RoleId === role.Id);
                role.initialiseRoleContextMapping(roleContext);
                return role;
            });
        } catch (error) {
            ErrorService.showError('An error occurred when loading the user\'s details', error);
        }
    };

    /**
     * Gets the user's brands available based on their roles
     * @returns {promise}
     */
    self.getUserBrands = function () {
        if (!PermissionService.user) {
            return [];

        }
        const deferred = $q.defer();

        let brandIds = _.compact(_.map(PermissionService.user.Roles || [], function (role) {
            if (role.BrandId) {
                return role.BrandId;
            }
        }));

        if (brandIds.length === 0) {
            deferred.resolve([]);
            return deferred.promise;
        }

        // Make sure there are no duplicates as this array is built up from multiple roles
        brandIds = _.uniq(brandIds);

        APIService.Brands.getBrandsByBrandIds(brandIds).then(
            function (results) {
                deferred.resolve(results.data);
            }
        ).catch(
            function () {
                deferred.reject('An error occurred when loading the user\'s brands');
            }
        );

        return deferred.promise;
    };

    /**
     * Gets the user's call centers available based on their roles
     * @returns {promise}
     */
    self.getUserCallCenters = function (callCenterIdList) {
        const deferred = $q.defer();

        const givenCallCenterIds = (callCenterIdList || []).filter(
            i => !!i && i > 0
        );
        const userCallCenterIds = PermissionService.user.Roles
            .filter(filteredRole => filteredRole && filteredRole.CallCenterId && filteredRole.CallCenterId > 0)
            .map(mappedRole => mappedRole.CallCenterId);

        const callCenterIds = [ ...new Set(givenCallCenterIds.concat(userCallCenterIds)) ];


        if (callCenterIds.length === 0) {
            deferred.resolve([]);
            return deferred.promise;
        }

        APIService.CallCenters.getCallCentersByCallCenterIds(callCenterIds).then(
            function (results) {
                deferred.resolve(results.data);
            }
        ).catch(
            function () {
                deferred.reject('An error occurred when loading the user\'s call centers');
            }
        );

        return deferred.promise;
    };

    /**
     * Gets the user's stores available based on their roles
     * @returns {promise}
     */
    self.getUserStores = function () {
        const deferred = $q.defer();

        let storeIds = [];
        _.each(PermissionService.user.Roles, function (role) {
            if (role.StoreIds.length > 0) {
                storeIds = storeIds.concat(role.StoreIds);
            }
        });

        if (storeIds.length === 0) {
            deferred.resolve([]);
            return deferred.promise;
        }

        // Make sure there are no duplicates as this array is built up from multiple roles
        storeIds = _.uniq(storeIds);

        APIService.Stores.getStoresByStoreIds(storeIds).then(
            function (results) {
                deferred.resolve(results.data);
            }
        ).catch(
            function () {
                deferred.reject('An error occurred when loading the user\'s stores');
            }
        );

        return deferred.promise;
    };

    /**
     * Gets the user's groups available based on their roles
     * @returns {promise}
     */
    self.getUserGroups = function () {
        const deferred = $q.defer();

        let groupIds = [];
        _.each(PermissionService.user.Roles, function (role) {
            if (role.GroupIds.length > 0) {
                groupIds = groupIds.concat(role.GroupIds);
            }
        });

        if (groupIds.length === 0) {
            deferred.resolve([]);
            return deferred.promise;
        }

        // Make sure there are no duplicates as this array is built up from multiple roles
        groupIds = _.uniq(groupIds);

        APIService.Groups.getGroupsByGroupIds(groupIds).then(
            function (results) {
                deferred.resolve(results.data);
            }
        ).catch(
            function () {
                deferred.reject('An error occurred when loading the user\'s groups');
            }
        );

        return deferred.promise;
    };

    /**
     * Gets the user's provinces available based on their roles
     * @returns {promise}
     */
    self.getUserProvinces = function () {
        const deferred = $q.defer();

        let provinceIds = [];
        _.each(PermissionService.user.Roles, function (role) {
            if (role.ProvinceIds.length > 0) {
                provinceIds = provinceIds.concat(role.ProvinceIds);
            }
        });

        if (provinceIds.length === 0) {
            deferred.resolve([]);
            return deferred.promise;
        }

        // Make sure there are no duplicates as this array is built up from multiple roles
        provinceIds = _.uniq(provinceIds);

        APIService.Provinces.getProvincesByProvinceIds(provinceIds).then(
            function (results) {
                deferred.resolve(results.data);
            }
        ).catch(
            function () {
                deferred.reject('An error occurred when loading the user\'s provinces');
            }
        );

        return deferred.promise;
    };

    self.saveUser = function (user) {
        const { storeId, callCenterId, brandId } = this.getUserStoreBrandAndCallCenterContexts();
        return APIService.Users.saveUser({
            ...user,
            BrandId: brandId,
            StoreId: storeId,
            CallCenterId: callCenterId
        });
    };

    self.createUser = (userModel) => {
        const { storeId, callCenterId, brandId } = this.getUserStoreBrandAndCallCenterContexts();
        return APIService.Users.createUser({
            ...userModel,
            brandId,
            storeId,
            callCenterId
        });
    }

    self.addUserRoles = function (userId, roles) {
        return APIService.Users.addUserRoles(userId, roles);
    };

    self.deleteUserRoles = function (userId, roles) {
        return APIService.Users.deleteUserRoles(userId, roles);
    }

    self.requestPasswordResetForUser = function (userId) {
        return APIService.Users.requestPasswordResetForUser(userId);
    }

    self.getUserStoreBrandAndCallCenterContexts = function () {
        const userContext = PermissionService.getUserContext();
        const storeId = userContext.StoreId;
        const callCenterId = userContext.CallCenterId;
        const brandId = !!storeId
            ? PermissionService.getBrandIdForStoreRole(storeId)
            : undefined;
        return {
            storeId: storeId || 0,
            callCenterId: callCenterId || 0,
            brandId: brandId || 0
        };
    }
};

angular.module('dashboard')
    .service('UsersService', [ 'APIService', 'PermissionService', '$q', 'ErrorService', UsersService ]);
